import React, {useState, useEffect, useRef, useContext} from 'react';
import {VendorContext} from '../../context/VendorProvider';
import {FaCheck} from 'react-icons/fa';
import {FiTrash2} from 'react-icons/fi';

const DualVendors = ({vendorId}) => {
  const {getVendors, updateVendor, getVendor} = useContext(VendorContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [currentVendor, setCurrentVendor] = useState({});
  const [consumerDetails, setConsumerDetails] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [showItems, setShowItems] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [consumerToDelete, setConsumerToDelete] = useState(null);

  useEffect(() => {
    const fetchCurrentVendor = async () => {
      const vendorData = await getVendor(vendorId);
      setCurrentVendor(vendorData);
      if (vendorData.consumers) {
        const consumerDetailsData = await Promise.all(
          vendorData.consumers.map((id) => getVendor(id))
        );
        setConsumerDetails(consumerDetailsData);
      }
    };

    fetchCurrentVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId]);

  const handleInputFocus = async () => {
    setIsDropdownOpen(true);
    inputRef.current.style.display = 'block';

    if (vendors.length === 0) {
      const allVendors = await getVendors();
      const excludedKeys = [vendorId, ...(currentVendor.consumers || [])];

      const filteredVendors = allVendors.filter(
        (vendor) =>
          !excludedKeys.includes(vendor.rowKey) &&
          !vendor.isProvider &&
          vendor.licensee.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setVendors(filteredVendors);
    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value.trim() === '') {
      setIsDropdownOpen(false);
    } else {
      setIsDropdownOpen(true);
    }
  };

  const handleVendorSelection = (vendorRowKey) => {
    setSelectedVendors((prevSelected) => {
      if (prevSelected.includes(vendorRowKey)) {
        return prevSelected.filter((key) => key !== vendorRowKey);
      } else {
        return [...prevSelected, vendorRowKey];
      }
    });
  };

  const handleAddSelectedVendors = async () => {
    setIsDropdownOpen(false);
    if (selectedVendors.length > 0) {
      try {
        const formData = {
          ...currentVendor,
          consumers: [
            ...new Set([...currentVendor.consumers, ...selectedVendors]),
          ],
        };
        await updateVendor(currentVendor.rowKey, formData);
        setSelectedVendors([]);
        const updatedVendorData = await getVendor(vendorId);
        setCurrentVendor(updatedVendorData);
        const newConsumerDetails = await Promise.all(
          updatedVendorData.consumers.map((id) => getVendor(id))
        );
        setConsumerDetails(newConsumerDetails);
      } catch (error) {
        console.error('Error updating consumers:', error);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowItems(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const openDeleteModal = (rowKey) => {
    setShowDeleteModal(true);
    setConsumerToDelete(rowKey);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setConsumerToDelete(null);
  };

  const handleDeleteConsumer = async () => {
    const updatedConsumers = currentVendor.consumers.filter(
      (consumerRowKey) => consumerRowKey !== consumerToDelete
    );
    const updatedVendor = {
      ...currentVendor,
      consumers: updatedConsumers,
    };

    try {
      await updateVendor(currentVendor.rowKey, updatedVendor);
      setCurrentVendor(await getVendor(vendorId));
      setConsumerDetails(
        await Promise.all(updatedConsumers.map((id) => getVendor(id)))
      );
      closeDeleteModal();
    } catch (error) {
      console.error('Error updating consumers:', error);
    }
  };

  return (
    <div
      ref={wrapperRef}
      className="rounded-lg bg-white p-4 dark:bg-stone-900"
    >
      <div className="relative mb-4 flex">
        <input
          ref={inputRef}
          type="text"
          className="flex-grow rounded-md border border-gray-300 bg-gray-100 px-2 py-1.5 dark:border-stone-700 dark:bg-stone-700 dark:text-gray-300"
          placeholder="Search Vendors..."
          value={searchTerm}
          onFocus={handleInputFocus}
          onChange={handleInputChange}
          autoComplete="off"
        />
        <button
          onClick={handleAddSelectedVendors}
          className="ml-2 rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
        >
          Add Selected
        </button>
        {isDropdownOpen && showItems && (
          <ul
            ref={dropdownRef}
            className="absolute z-10 mt-12 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white dark:border-stone-700 dark:bg-stone-800 dark:text-gray-300"
          >
            {showItems &&
              vendors.map((vendor) => (
                <li
                  key={vendor.rowKey}
                  className="flex cursor-pointer items-center justify-between p-2 hover:bg-gray-200 dark:hover:bg-stone-600"
                  onClick={() => handleVendorSelection(vendor.rowKey)}
                >
                  <div className="flex items-center">
                    <div className="mr-2 flex h-5 w-5 items-center justify-center">
                      {selectedVendors.includes(vendor.rowKey) && (
                        <FaCheck className="text-green-500"/>
                      )}
                    </div>
                    <span>
                      {vendor.licensee} ({vendor.vendorCode})
                    </span>
                  </div>
                </li>
              ))}
          </ul>
        )}
      </div>
      <div className="mt-4">
        <div className="mt-2">
          <table className="min-w-full text-left text-sm text-gray-500 dark:text-gray-300">
            <thead
              className="sticky top-0 bg-white text-xs uppercase text-gray-700 dark:bg-stone-900 dark:text-gray-300">
            <tr>
              <th scope="col" className="py-3 pl-6">
                Consumer
              </th>
              <th scope="col" className="py-3 pl-6">
                Actions
              </th>
            </tr>
            </thead>
            <tbody>
            {consumerDetails.map((consumer) => (
              <tr
                key={consumer.rowKey}
                className={` ${
                  isDropdownOpen
                    ? 'bg-gray-200 opacity-50 hover:bg-gray-200 dark:border-stone-900 dark:bg-stone-950 dark:hover:bg-stone-900'
                    : 'border-b bg-white hover:bg-gray-50 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-600'
                } `}
              >
                <td className="px-6 py-4">{consumer.licensee}</td>
                <td>
                  <button
                    onClick={() => openDeleteModal(consumer.rowKey)}
                    className={`danger-hollow ${isDropdownOpen ? 'border-red-900 text-red-900' : 'border-red-500 text-red-500'} mr-2 flex items-center`}
                  >
                    <FiTrash2 className="mr-2 h-3 w-3"/>
                    <span>Delete</span>
                  </button>
                </td>
              </tr>
            ))}
            {showDeleteModal && (
              <div
                className="fixed inset-0 flex h-full w-full items-center justify-center overflow-y-auto bg-stone-950 bg-opacity-75"
                id="delete-modal"
              >
                <div className="relative mx-auto w-96 rounded-md bg-white p-5 shadow-lg dark:bg-stone-800">
                  <div className="mt-3 text-center">
                    <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                      Delete Consumer
                    </h3>
                    <div className="mt-2 px-7 py-3">
                      <p className="text-sm text-gray-500 dark:text-gray-300">
                        Are you sure you want to delete this consumer?
                      </p>
                    </div>
                    <div className="items-center px-4 py-3">
                      <button
                        id="delete-ok-btn"
                        className="rounded-md bg-red-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                        onClick={handleDeleteConsumer}
                      >
                        Delete
                      </button>
                      <button
                        className="ml-3 rounded-md bg-gray-300 px-4 py-2 text-base font-medium text-gray-900 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400"
                        onClick={closeDeleteModal}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DualVendors;
